import { Inbox, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Inline busy indicator with a text alternative for screen readers. */
export function Spinner({
  label = 'Loading',
  className,
}: {
  readonly label?: string;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-fg-subtle', className)}>
      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/**
 * Skeleton placeholder. aria-hidden, because a screen reader should hear the "Loading"
 * from the adjacent role=status, not a description of grey rectangles.
 */
export function Skeleton({ className }: { readonly className?: string }): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn('block animate-pulse rounded bg-surface-sunken', className)}
    />
  );
}

export interface EmptyStateProps {
  readonly title: string;
  readonly description?: string | undefined;
  readonly action?: React.ReactNode | undefined;
  readonly icon?: React.ReactNode | undefined;
  readonly className?: string | undefined;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-14 text-center', className)}>
      <span className="flex size-11 items-center justify-center rounded-full border border-border bg-surface-sunken text-fg-subtle">
        {icon ?? <Inbox className="size-5" aria-hidden="true" />}
      </span>
      <div>
        <p className="text-sm font-semibold text-fg">{title}</p>
        {description !== undefined ? (
          <p className="mx-auto mt-1 max-w-md text-xs text-fg-subtle">{description}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}
